import type { QueryClient } from '@tanstack/react-query'
import io, { type Socket } from 'socket.io-client'
import { create } from 'zustand'

import type { RobotProgressEvent, RobotStatusDto } from '@brickup/shared/dto'
import {
  fetchRobotStatus,
  ROBOT_STATUS_KEY,
  setRobotSocketOpen,
} from '@/api/matching-robot'
import { SEARCH_MANDATES_KEY } from '@/api/query-keys'

// Private module state — not in store state to avoid spurious re-renders
let _socket: Socket | null = null
let _isSingleMandateRun = false

/**
 * The robot's socket and the progress of the run it reports. Whether a run is
 * going is part of the robot's status: the socket writes it into that cache
 * entry, where `useRobotStatus` reads it.
 */
type RobotWebSocketState = {
  isConnected: boolean
  robotProgress: RobotProgressEvent | null
  connect: (queryClient: QueryClient) => () => void
}

export const useRobotWebSocketStore = create<RobotWebSocketState>()(set => ({
  isConnected: false,
  robotProgress: null,

  // Single owner expected — called once in Matching.tsx on mount
  connect: (queryClient: QueryClient) => {
    if (_socket !== null) {
      return () => {}
    }

    const setRunning = (running: boolean) =>
      queryClient.setQueryData<RobotStatusDto>(ROBOT_STATUS_KEY, old =>
        old ? { ...old, running } : old,
      )

    _socket = io(`${import.meta.env.VITE_API_URL}/matching-robot`, {
      withCredentials: true,
    })

    _socket.on('connect', () => {
      set({ isConnected: true })
      setRobotSocketOpen(true)
      // A run that started while no socket was open has reported nothing
      // here, so a stale status is read again.
      void queryClient.ensureQueryData({
        queryKey: ROBOT_STATUS_KEY,
        queryFn: fetchRobotStatus,
        revalidateIfStale: true,
      })
    })
    _socket.on('disconnect', () => {
      set({ isConnected: false })
      setRobotSocketOpen(false)
    })

    _socket.on('robot:started', data => {
      _isSingleMandateRun = data.type === 'mandate' || data.totalMandates === 1
      setRunning(true)
    })

    _socket.on('robot:progress', (data: RobotProgressEvent) => {
      set({ robotProgress: data })
      setRunning(true)
    })

    _socket.on('robot:mandate:complete', data => {
      queryClient.invalidateQueries({
        queryKey: [...SEARCH_MANDATES_KEY, data.mandateId, 'matches'],
      })

      if (_isSingleMandateRun) {
        queryClient.invalidateQueries({ queryKey: SEARCH_MANDATES_KEY })
      }
    })

    _socket.on('robot:completed', () => {
      set({ robotProgress: null })
      setRunning(false)
      queryClient.invalidateQueries({ queryKey: ROBOT_STATUS_KEY })
      if (!_isSingleMandateRun) {
        queryClient.invalidateQueries({ queryKey: SEARCH_MANDATES_KEY })
      }
      _isSingleMandateRun = false
    })

    // A mandate that fails is reported with `willContinue` and the run goes
    // on to the next one.
    _socket.on('robot:error', data => {
      if (data.willContinue) return
      set({ robotProgress: null })
      setRunning(false)
      _isSingleMandateRun = false
    })

    _socket.on('robot:status:changed', () => {
      queryClient.invalidateQueries({ queryKey: SEARCH_MANDATES_KEY })
    })

    // Return cleanup function
    return () => {
      _socket?.removeAllListeners()
      _socket?.close()
      _socket = null
      _isSingleMandateRun = false
      setRobotSocketOpen(false)
      set({ isConnected: false, robotProgress: null })
    }
  },
}))
